import { useEffect, useState } from 'react';
import { Project } from 'api/project';
import { TimesheetEntryType } from 'api/timesheets';
import {
  TimesheetReport,
  TimesheetReportsSummary,
  TimesheetReportType,
} from 'api/timesheets/reports/types';
import { logger } from 'services';

export const useReports = (reportApi, projects?: Project[]) => {
  const [timesheetReports, setTimesheetReports] = useState<TimesheetReport[]>(
    [],
  );

  const [isReportsLoading, setIsReportsLoading] = useState(true);

  useEffect(() => {
    setIsReportsLoading(true);
    reportApi()
      .then((response) => {
        setIsReportsLoading(false);
        setTimesheetReports(response);
      })
      .catch((error) => {
        logger.error('Error is fetching reports', { error });
        setIsReportsLoading(false);
      });
  }, [reportApi]);

  const timesheetReportsSummary: TimesheetReportsSummary =
    timesheetReports.reduce(
      (result, report) => {
        if (report.type !== TimesheetReportType.Category) {
          const { categories } = report;

          categories.forEach((_category) => {
            const matchCategory = result.categories?.find(
              (x) => x.categoryId === _category.categoryId,
            );

            if (matchCategory) {
              matchCategory.totalMinutes += _category.totalMinutes;
            } else {
              result.categories?.push({ ..._category });
            }
          });
        }

        let budgetHours = 0;

        if (report.type === TimesheetReportType.Project) {
          const { project } = report;

          if (project) {
            const budgetFieldValue = projects?.find(
              (a) => a.projectId === project.projectId,
            )?.budgetedHours;

            budgetHours = budgetFieldValue || 0;
          }
        }
        let effort = 0;

        if (report.type === TimesheetReportType.Task) {
          effort =
            report.task.entryType === TimesheetEntryType.TaskEntry
              ? report.task.effort ?? 0
              : 0;
        }

        let allocatedHours = 0;

        if (
          report.type === TimesheetReportType.Project ||
          report.type === TimesheetReportType.User
        ) {
          allocatedHours = report.allocatedMinutes;
        }

        let timeOffInfo = 0;

        if (report.type === TimesheetReportType.User) {
          timeOffInfo = report.timeOffMinutes;
        }

        return {
          billableMinutes: result.billableMinutes + report.billableMinutes,
          nonBillableMinutes:
            result.nonBillableMinutes + report.nonBillableMinutes,
          effort: (result.effort ?? 0) + effort,
          budgetedHours: (result.budgetedHours ?? 0) + budgetHours,
          categories: result.categories,
          allocatedMinutes: result.allocatedMinutes + allocatedHours,
          timeOffMinutes: result.timeOffMinutes + timeOffInfo,
        };
      },
      {
        billableMinutes: 0,
        nonBillableMinutes: 0,
        budgetedHours: 0,
        effort: 0,
        categories: [],
        allocatedMinutes: 0,
        timeOffMinutes: 0,
      } as TimesheetReportsSummary,
    );

  return {
    isReportsLoading,
    timesheetReports,
    timesheetReportsSummary,
  };
};
